import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { FormikContextType } from 'formik';

interface SelectFormikProps {
  formik: FormikContextType<any>;
  name: string;
  label: string;
  options: any[];
  labelStyle?: CSSProperties;
  validationSentence?: string;
  onChange?: (e: SelectChangeEvent) => void;
  className?: string;
  placeholder?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

export default function SelectFormik({
  formik,
  name,
  label,
  labelStyle,
  validationSentence,
  onChange,
  className,
  options,
  placeholder,
  style,
  disabled,
}: SelectFormikProps) {
  const { t } = useTranslation();
  const isError = !!(formik.touched[name] && formik.errors[name]);
  const handleChange = (e: SelectChangeEvent) => {
    if (onChange) {
      onChange(e);
      return;
    }
    formik.handleChange(e);
  };
  const bankCodeMap = useMemo(() => {
    return options.reduce(
      (acc, { code, name }) => ({
        ...acc,
        [code]: name,
      }),
      {}
    );
  }, [options]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: { maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP },
    },
    MenuListProps: { style: { padding: 0 } },
  };

  return (
    <SWrapper $isError={isError} className={className} style={style}>
      {/* <label className="label" style={labelStyle}>
        {label}
      </label> */}
      <Select
        variant="outlined"
        name={name}
        value={formik.values[name]}
        disabled={disabled}
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <span>{placeholder}</span>;
          } else {
            return bankCodeMap[selected];
          }
        }}
        onChange={handleChange}
        sx={{
          width: '100%',
          mt: 1,
          '&.Mui-focused': { borderColor: '#000' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E3E5E8' },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#E3E5E8' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E3E5E8' },
          '& .MuiSelect-select.MuiSelect-outlined ': {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '15px 16px',
            '& .MuiRadio-root': { display: 'none' },
          },
          '& .MuiSelect-icon': { color: '#131415' },
          '& .MuiSelect-icon.Mui-disabled': { color: '#bcbcbc' },
          '& .MuiSelect-nativeInput': { opacity: 0 },
        }}
        MenuProps={MenuProps}
      >
        {options.map(({ code, name }) => {
          return (
            <MenuItem key={code} value={code}>
              {name}
            </MenuItem>
          );
        })}
      </Select>
      {validationSentence && (
        <div className="validation-sentence">
          {t(validationSentence)
            ?.split('\n')
            .map((text) =>
              isError ? (
                <p key={text} className="error">
                  {text}
                </p>
              ) : (
                <p key={text} className="default">
                  {text}
                </p>
              )
            )}
        </div>
      )}
    </SWrapper>
  );
}

const SWrapper = styled.div<{ $isError: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;

  .label {
    font-size: 13px;
    color: ${({ theme }) => theme.color.text.TXT_2};
  }

  .validation-sentence {
    height: 17px;
    margin-top: 6px;
    width: 100%;
    font-size: 12px;
    list-style: inside;
    ${({ theme }) => theme.font.caption1_m};

    .error {
      padding: 0 12px;
      color: ${({ theme }) => theme.color.static.warning};
    }
    .default {
      padding: 0 12px;
      color: ${({ theme }) => theme.color.text.TXT_1};
    }
  }
`;
